import managementApi from '@utils/managementApi';
export interface IUserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  accountCreatedAt: number;
  mentorId: string;
  mentorSlug: string;
}

export interface UpdateUserResponse {
  success: boolean;
  message: string;
  data: {
    isSuccess: boolean;
    errorCode: string;
    payload: IUserData;
  };
}

export interface IOrganizationMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  state: string;
  roles: { name: string }[];
  joinedAt: string;
  isOwner: boolean;
}

export interface IGetOrganizationMembersResponse {
  items: IOrganizationMember[];
  page_index: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  filtered_count: number;
}

export interface IInvitee {
  email: string;
  firstName: string;
  lastName: string;
}

export interface IInviter {
  id: string;
}

export interface IInviteUsersRequest {
  organizationId: string;
  subscriptionId: string;
  invitee: IInvitee;
  inviter: IInviter;
  url: string;
  roles: string[];
}

export interface IInviteUsersResponse {
  success: boolean;
  message?: string;
}
export interface IInvitation {
  id: string;
  kind: string;
  status: string;
  invitee: IInvitee;
  inviter: IInviter;
  organization: {
    id: string;
    name: string;
  };
  createdAt: string;
  expiredAt: string;
}

export interface IGetOrganizationInvitationsResponse {
  items: IInvitation[];
  page_index: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  filtered_count: number;
}

export async function getOrganizationMembers(
  organizationId: string,
): Promise<IGetOrganizationMembersResponse> {
  try {
    const response = await managementApi.get<IGetOrganizationMembersResponse>(
      `/v2/organizations/${organizationId}/users`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching organization members:', error);
    throw error;
  }
}

export async function inviteUsersToOrganization(
  data: IInviteUsersRequest,
): Promise<IInviteUsersResponse> {
  try {
    const response = await managementApi.post<IInviteUsersResponse>(
      `/v2/organizations/${data.organizationId}/invitations`,
      {
        subscriptionId: data.subscriptionId,
        invitee: data.invitee,
        inviter: data.inviter,
        url: data.url,
        roles: data.roles,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error inviting users to organization:', error);
    throw error;
  }
}

export async function getOrganizationInvitations(
  organizationId: string,
): Promise<IGetOrganizationInvitationsResponse> {
  try {
    const response =
      await managementApi.get<IGetOrganizationInvitationsResponse>(
        `/v2/organizations/${organizationId}/invitations`,
      );
    return response.data;
  } catch (error) {
    console.error('Error fetching organization invitations:', error);
    throw error;
  }
}

export async function getUser(): Promise<{ data: IUserData }> {
  try {
    const response = await managementApi.get<{ data: IUserData }>(
      '/v1/users/current',
    );
    return response.data;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
}

export async function updateUser(
  userSub: string,
  data: { firstName: string; lastName: string },
): Promise<UpdateUserResponse> {
  try {
    const response = await managementApi.patch<UpdateUserResponse>(
      `/v2/users/${userSub}`,
      {
        first_name: data.firstName,
        last_name: data.lastName,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
}

export interface IRemindInvitationRequest {
  organizationId: string;
  inviteId: string;
}

export interface IRemindInvitationResponse {
  success: boolean;
  message?: string;
}

export async function remindInvitation(
  data: IRemindInvitationRequest,
): Promise<IRemindInvitationResponse> {
  try {
    const response = await managementApi.post<IRemindInvitationResponse>(
      `/v2/organizations/${data.organizationId}/invitations/${data.inviteId}/remind`,
    );
    return response.data;
  } catch (error) {
    console.error('Error sending invitation reminder:', error);
    throw error;
  }
}

export interface IRevokeInvitationRequest {
  organizationId: string;
  inviteId: string;
}

export interface IRevokeInvitationResponse {
  success: boolean;
  message?: string;
}

export async function revokeInvitation(
  data: IRevokeInvitationRequest,
): Promise<IRevokeInvitationResponse> {
  try {
    const response = await managementApi.post<IRevokeInvitationResponse>(
      `/v2/organizations/${data.organizationId}/invitations/${data.inviteId}/revoke`,
    );
    return response.data;
  } catch (error) {
    console.error('Error revoking invitation:', error);
    throw error;
  }
}

export interface IRemoveUserFromOrganizationRequest {
  organizationId: string;
  userId: string;
}

export interface IRemoveUserFromOrganizationResponse {
  success: boolean;
  message?: string;
}

export async function removeUserFromOrganization(
  data: IRemoveUserFromOrganizationRequest,
): Promise<IRemoveUserFromOrganizationResponse> {
  try {
    const response =
      await managementApi.delete<IRemoveUserFromOrganizationResponse>(
        `/v2/organizations/${data.organizationId}/users/${data.userId}`,
      );
    return response.data;
  } catch (error) {
    console.error('Error removing user from organization:', error);
    throw error;
  }
}

export interface IUpdateDefaultOrganizationRequest {
  organizationId: string;
}

export interface IUpdateDefaultOrganizationResponse {
  success: boolean;
  message?: string;
}

export async function updateDefaultOrganization(
  data: IUpdateDefaultOrganizationRequest,
): Promise<IUpdateDefaultOrganizationResponse> {
  try {
    const response =
      await managementApi.patch<IUpdateDefaultOrganizationResponse>(
        '/v2/users/me/default-organization',
        { organizationId: data.organizationId },
      );
    return response.data;
  } catch (error) {
    console.error('Error updating default organization:', error);
    throw error;
  }
}
